// src/legal/identityCache.ts
//
// The user record is cached twice: in the auth provider's module cache and in
// the `useGetIdentity()` query. Both must change together, or the next
// `getIdentity()` call would bring the old `legal` state back.
import type { QueryClient } from '@tanstack/react-query';
import {
    refreshCachedUser,
    setCachedUserLegal,
} from '../providers/firebase/authProvider';
import type { LegalState } from './legalState';

const IDENTITY_KEY = ['auth', 'getIdentity'];

/** Continue with the `legal` state the server returned, without a reload. */
export function applyLegalState(queryClient: QueryClient, legal: LegalState) {
    setCachedUserLegal(legal);
    queryClient.setQueryData(IDENTITY_KEY, (identity: object | undefined) =>
        identity ? { ...identity, legal } : identity
    );
}

/**
 * Re-read `users/{uid}` and hand the fresh record to everyone. Returns it, or
 * undefined when there is none to be had.
 */
export async function rereadIdentity(
    queryClient: QueryClient
): Promise<object | undefined> {
    const user = await refreshCachedUser();
    if (user) queryClient.setQueryData(IDENTITY_KEY, user);
    return user ?? undefined;
}
